import React, { Fragment } from 'react';
import { RootState } from '../redux/store';
import { changePageFilter } from '../redux/actions/PageActions';
import { connect, ConnectedProps } from 'react-redux';
import { FormicaProps, withFormica } from '../helpers/withFormica';
import ComponentName from '../constant/ComponentName';
import { OverlayPanel } from 'primereact/overlaypanel';
import ServiceContainer from '../config/ServiceContainer';
import { ServiceNames } from '../constant/ServiceNames';
import { ComponentState, setSelectedItems } from '../redux/actions/ComponentActions';
import { ActionJsonModel, ActionSchema } from '../domain/collection/rule/action/ActionDomains';
import ActionService from '../service/ActionService';
import RoleHelper from '../RoleHelper';
import CustomDismissible from '../components/custom/CustomDismissible';
import StandardContainerComponent, { StandardSubscriptionComponentArgs } from '../helpers/StandardContainerComponent';
import ActionListViewerComponent from '../components/action/ActionListViewerComponent';
import CollectionNames from '../constant/CollectionNames';
import Icon from 'icons/Icon';
import LanguageUtil from '../util/LanguageUtil';
import { BulkActionDependencyError } from 'redux/reducers/BulkActionReducer';

class ActionContainer extends StandardContainerComponent<Props, State, ActionJsonModel, ActionSchema> {
  private createOverlayRef: OverlayPanel | null = null;

  private readonly actionService: ActionService = ServiceContainer.getInstance().get(ServiceNames.ACTION);

  constructor(props: Props) {
    const args: StandardSubscriptionComponentArgs = {
      component: ComponentName.ACTION,
      withSchema: true,
      itemCollection: CollectionNames.ACTIONS,
      label: LanguageUtil.get({ id: 'common.label.actions', defaultMessage: 'Actions' }),
    };
    super(props, args);
  }

  public render() {
    return (
      <Fragment>
        {super.render()}
        <OverlayPanel ref={(el) => (this.createOverlayRef = el)}>{this.getCreateItems()}</OverlayPanel>
      </Fragment>
    );
  }

  protected getListView(): React.ReactNode {
    const { items, itemBuilderView, permissions } = this.state;
    return (
      <ActionListViewerComponent
        actions={items}
        views={itemBuilderView?.getListViews()}
        currentModal={itemBuilderView?.getLastCurrent()}
        openAction={this.openComponent}
        onSortChange={this.onSortChange}
        permissions={permissions}
      />
    );
  }

  protected async prepareCount() {
    const count = await this.actionService.count(this.getCountFilter().build());
    this.setState({ totalRecord: count });
  }

  protected getActionsFromModal(): string[] {
    const {
      component: { selectedComponentIds },
    } = this.props;
    return selectedComponentIds.map((action: ActionJsonModel) => action.name);
  }

  protected async deployAll(): Promise<BulkActionDependencyError[]> {
    return await this.actionService.bulkAction(this.getActionsFromModal(), 'Deploy');
  }

  protected async undeployAll(): Promise<BulkActionDependencyError[]> {
    return await this.actionService.bulkAction(this.getActionsFromModal(), 'Undeploy');
  }

  protected async removeAll(): Promise<BulkActionDependencyError[]> {
    return await this.actionService.bulkAction(this.getActionsFromModal(), 'Remove');
  }

  protected openCreatePage = (e: any) => {
    if (!RoleHelper.canEdit('Action')) return;
    this.createOverlayRef?.toggle(e);
  };

  protected openComponent = (name: string) => {
    const { items } = this.state;
    const {
      router: { navigate },
    } = this.props;
    const item = items.find((i) => i.name == name);
    if (item == undefined) return;
    navigate(`${item.template}/${item.name}`);
  };

  private navigateToCreate = (path: string) => {
    const {
      router: { navigate },
    } = this.props;
    navigate(`${path}/create`);
  };

  private getCreateItems() {
    return (
      <CustomDismissible id={`item-tools-${this.id}`} onDismiss={() => this.createOverlayRef?.hide()}>
        <div className={'flex flex-col gap-y-2 text-xs px-2 py-2 items-start justify-center w-[280px]'}>
          {RoleHelper.canEdit('Action') && (
            <p
              onClick={() => this.navigateToCreate('email')}
              className={'flex items-center w-full hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer'}
              data-testid={`${ComponentName.ACTION}_create_email`}
            >
              <Icon icon={'mail'} size={14} />
              {LanguageUtil.get({ id: 'common.label.email', defaultMessage: 'Email' })}
            </p>
          )}
          {RoleHelper.canEdit('Action') && (
            <p
              onClick={() => this.navigateToCreate('api')}
              className={'flex items-center w-full hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer'}
              data-testid={`${ComponentName.ACTION}_create_api`}
            >
              <Icon icon={'api'} size={14} />
              {LanguageUtil.get({ id: 'common.label.api', defaultMessage: 'API' })}
            </p>
          )}
          {RoleHelper.canEdit('Action') && (
            <p
              onClick={() => this.navigateToCreate('slack')}
              className={'flex items-center w-full hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer'}
              data-testid={`${ComponentName.ACTION}_create_slack`}
            >
              <Icon icon={'slack'} size={14} />
              {LanguageUtil.get({ id: 'common.label.slack', defaultMessage: 'Slack' })}
            </p>
          )}
          {RoleHelper.canEdit('Action') && (
            <p
              onClick={() => this.navigateToCreate('discord')}
              className={'flex items-center w-full hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer'}
              data-testid={`${ComponentName.ACTION}_create_discord`}
            >
              <Icon icon={'discord'} size={14} />
              {LanguageUtil.get({ id: 'common.label.discord', defaultMessage: 'Discord' })}
            </p>
          )}
          {RoleHelper.canEdit('Action') && (
            <p
              onClick={() => this.navigateToCreate('jira')}
              className={'flex items-center w-full hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer'}
              data-testid={`${ComponentName.ACTION}_create_jira`}
            >
              <Icon icon={'jira'} size={14} />
              {LanguageUtil.get({ id: 'common.label.jira', defaultMessage: 'Jira' })}
            </p>
          )}
          {RoleHelper.canEdit('Action') && (
            <p
              onClick={() => this.navigateToCreate('microsoft-teams')}
              className={'flex items-center w-full hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer'}
              data-testid={`${ComponentName.ACTION}_create_microsoft-teams`}
            >
              <Icon icon={'teams'} size={14} />
              {LanguageUtil.get({ id: 'common.label.teams', defaultMessage: 'Micorsoft Teams' })}
            </p>
          )}
          {RoleHelper.canEdit('Action') && (
            <p
              onClick={() => this.navigateToCreate('add-to-list')}
              className={'flex items-center w-full hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer'}
              data-testid={`${ComponentName.ACTION}_create_add-to-list`}
            >
              <Icon icon={'add-to-list'} size={14} />
              {LanguageUtil.get({ id: 'common.label.addToList', defaultMessage: 'Add to List' })}
            </p>
          )}
          {RoleHelper.canEdit('Action') && (
            <p
              onClick={() => this.navigateToCreate('remove-from-list')}
              className={'flex items-center w-full hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer'}
              data-testid={`${ComponentName.ACTION}_create_remove-from-list`}
            >
              <Icon icon={'remove-from-list'} size={14} />
              {LanguageUtil.get({ id: 'common.label.removeFromList', defaultMessage: 'Remove From List' })}
            </p>
          )}
          {RoleHelper.canEdit('Action') && (
            <p
              onClick={() => this.navigateToCreate('send-transaction')}
              className={'flex items-center w-full hover:bg-primary hover:text-white rounded px-1 py-1 indent-1 hover:cursor-pointer'}
              data-testid={`${ComponentName.ACTION}_create_send-transaction`}
            >
              <Icon icon={'sandbox'} size={14} />
              {LanguageUtil.get({ id: 'common.label.sendTransaction', defaultMessage: 'Send Transaction' })}
            </p>
          )}
        </div>
      </CustomDismissible>
    );
  }
}

interface Props extends FormicaProps, ReduxProps {}

class State {}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  itemView: state.page.itemView,
  currentCategory: state.page.currentCategory,
  pagePath: state.page.pagePath,
  component: state.component as ComponentState,
  user: state.auth.user,
  attributes: state.auth.attributes,
});
const mapDispatchToProps = { setSelectedItems, changePageFilter };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ActionContainer));
